import React, { useState, useEffect, useCallback, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';

const API = import.meta.env.VITE_API_URL;

interface DeviceInfo {
  id: string;
  name: string;
  model: string | null;
  app_version: string | null;
  platform: string | null;
  status: string;
  last_seen: string | null;
  fcm_registered: number;
  fcm_token_updated_at: string | null;
  created_at: string;
  updated_at: string;
}

interface HealthRecord {
  id: string;
  device_id: string;
  battery_level: number | null;
  charging: number | null;
  storage_free: number | null;
  memory_free: number | null;
  queue_size: number | null;
  sync_state: string | null;
  network_type: string | null;
  app_version: string | null;
  worker_source: string | null;
  created_at: string;
}

interface CommandStats {
  pending_commands: number;
  total_commands: number;
  last_command_at: string | null;
  lastResult: {
    status: string;
    created_at: string;
    command_type: string;
  } | null;
}

interface SmsStats {
  total_messages: number;
  last_sync_at: string | null;
}

interface HealthData {
  device: DeviceInfo;
  health: HealthRecord[];
  commands: CommandStats;
  sms: SmsStats;
}

function formatTime(ts: string | null | undefined): string {
  if (!ts) return '—';
  const date = new Date(ts);
  if (isNaN(date.getTime())) return ts;
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHrs = Math.floor(diffMs / 3600000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHrs < 24) return `${diffHrs}h ago`;
  return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function StatusDot({ status }: { status: string }): ReactElement {
  const color = status === 'online' ? 'var(--status-success)' : 'var(--status-error)';
  return <span style={{ color, marginRight: '6px' }}>●</span>;
}

export default function DeviceHealthPage(): ReactElement {
  const navigate = useNavigate();
  const [deviceId, setDeviceId] = useState<string | null>(null);
  const [devices, setDevices] = useState<{ id: string; name: string }[]>([]);
  const [healthData, setHealthData] = useState<HealthData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const token = localStorage.getItem('velinko_token');

  const fetchDevices = useCallback(async () => {
    if (!token) return;
    try {
      const resp = await fetch(`${API}/devices`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) return;
      const json = await resp.json();
      const devs = json.data?.devices || [];
      setDevices(devs);
      if (devs.length > 0 && !deviceId) {
        setDeviceId(devs[0].id);
      }
    } catch {
      // silent
    }
  }, [token, deviceId]);

  const fetchHealth = useCallback(async (id: string) => {
    if (!token || !id) return;
    try {
      const resp = await fetch(`${API}/devices/${id}/health-details`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (resp.status === 401) {
        navigate('/login');
        return;
      }
      if (!resp.ok) {
        setError(`HTTP ${resp.status}`);
        return;
      }
      const json = await resp.json();
      setHealthData(json.data);
      setError(null);
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  useEffect(() => {
    if (deviceId) {
      fetchHealth(deviceId);
    }
  }, [deviceId, fetchHealth]);

  useEffect(() => {
    if (!autoRefresh || !deviceId) return;
    const interval = setInterval(() => fetchHealth(deviceId), 15000);
    return () => clearInterval(interval);
  }, [autoRefresh, deviceId, fetchHealth]);

  const section: React.CSSProperties = {
    background: 'var(--bg-secondary)',
    border: '1px solid var(--border)',
    borderRadius: '6px',
    padding: '12px 16px',
    marginBottom: '12px',
  };

  const sectionTitle: React.CSSProperties = {
    fontSize: '11px',
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    color: 'var(--text-muted)',
    marginBottom: '8px',
  };

  const row: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '3px 0',
    fontSize: '13px',
  };

  const label: React.CSSProperties = {
    color: 'var(--text-muted)',
  };

  const value: React.CSSProperties = {
    fontFamily: 'monospace',
    color: 'var(--text-primary)',
  };

  if (!token) {
    return <div style={{ padding: '20px' }}>Not authenticated. <a href="/login">Login</a></div>;
  }

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button
            onClick={() => navigate('/')}
            style={{
              background: 'none',
              border: '1px solid var(--border)',
              color: 'var(--text-primary)',
              padding: '4px 12px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '13px',
            }}
          >
            ← Dashboard
          </button>
          <h1 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>Device Health</h1>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {devices.length > 1 && (
            <select
              value={deviceId || ''}
              onChange={(e) => { setDeviceId(e.target.value); setLoading(true); }}
              style={{
                background: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border)',
                padding: '4px 8px',
                borderRadius: '4px',
                fontSize: '13px',
              }}
            >
              {devices.map((d) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
          )}
          <label style={{ fontSize: '12px', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />
            Auto-refresh
          </label>
          <button
            onClick={() => { if (deviceId) fetchHealth(deviceId); }}
            style={{
              background: 'var(--accent-primary)',
              color: '#fff',
              border: 'none',
              padding: '4px 12px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '13px',
            }}
          >
            Refresh
          </button>
        </div>
      </div>

      {error && <div style={{ color: 'var(--status-error)', padding: '8px', marginBottom: '12px' }}>Error: {error}</div>}
      {loading && <div style={{ color: 'var(--text-muted)', padding: '20px', textAlign: 'center' }}>Loading...</div>}

      {healthData && !loading && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          {/* DEVICE */}
          <div style={section}>
            <div style={sectionTitle}>Device</div>
            <div style={row}><span style={label}>Name</span><span style={value}>{healthData.device.name}</span></div>
            <div style={row}><span style={label}>Model</span><span style={value}>{healthData.device.model || '—'}</span></div>
            <div style={row}><span style={label}>Device ID</span><span style={{ ...value, fontSize: '11px' }}>{healthData.device.id}</span></div>
            <div style={row}><span style={label}>App Version</span><span style={value}>{healthData.device.app_version || '—'}</span></div>
            <div style={row}><span style={label}>Platform</span><span style={value}>{healthData.device.platform || 'android'}</span></div>
            <div style={row}><span style={label}>Registered</span><span style={value}>{formatTime(healthData.device.created_at)}</span></div>
          </div>

          {/* CONNECTIVITY */}
          <div style={section}>
            <div style={sectionTitle}>Connectivity</div>
            <div style={row}>
              <span style={label}>Status</span>
              <span style={value}>
                <StatusDot status={healthData.device.status} />
                {healthData.device.status}
              </span>
            </div>
            <div style={row}><span style={label}>Last Seen</span><span style={value}>{formatTime(healthData.device.last_seen)}</span></div>
            <div style={row}><span style={label}>Updated</span><span style={value}>{formatTime(healthData.device.updated_at)}</span></div>
          </div>

          {/* BACKGROUND / HEALTH */}
          <div style={section}>
            <div style={sectionTitle}>Background Health</div>
            {healthData.health.length === 0 ? (
              <div style={{ color: 'var(--text-muted)', fontSize: '12px', padding: '8px 0' }}>No health records yet</div>
            ) : (
              <>
                {(() => {
                  const latest = healthData.health[0];
                  if (!latest) return null;
                  return (
                    <>
                      <div style={row}><span style={label}>Battery</span><span style={value}>{latest.battery_level !== null && latest.battery_level !== undefined ? `${latest.battery_level}%` : '—'}{latest.charging ? ' ⚡' : ''}</span></div>
                      <div style={row}><span style={label}>Queue Size</span><span style={value}>{latest.queue_size ?? '—'}</span></div>
                      <div style={row}><span style={label}>Sync State</span><span style={value}>{latest.sync_state || '—'}</span></div>
                      <div style={row}><span style={label}>Network</span><span style={value}>{latest.network_type || '—'}</span></div>
                      <div style={row}><span style={label}>Worker</span><span style={value}>{latest.worker_source || '—'}</span></div>
                      <div style={row}><span style={label}>Last Report</span><span style={value}>{formatTime(latest.created_at)}</span></div>
                    </>
                  );
                })()}
                <details style={{ marginTop: '8px' }}>
                  <summary style={{ fontSize: '11px', color: 'var(--text-muted)', cursor: 'pointer' }}>
                    History ({healthData.health.length} records)
                  </summary>
                  <div style={{ maxHeight: '200px', overflowY: 'auto', marginTop: '6px' }}>
                    {healthData.health.map((h, i) => (
                      <div key={i} style={{ ...row, borderTop: '1px solid var(--border)', paddingTop: '4px', marginTop: '4px' }}>
                        <span style={label}>{formatTime(h.created_at)}</span>
                        <span style={value}>
                          {h.battery_level !== null && h.battery_level !== undefined ? `${h.battery_level}%` : '-'}
                          {h.charging ? '⚡' : ''}
                          {h.worker_source ? ` [${h.worker_source}]` : ''}
                        </span>
                      </div>
                    ))}
                  </div>
                </details>
              </>
            )}
          </div>

          {/* SMS */}
          <div style={section}>
            <div style={sectionTitle}>SMS</div>
            <div style={row}><span style={label}>Total Messages</span><span style={value}>{healthData.sms?.total_messages ?? 0}</span></div>
            <div style={row}><span style={label}>Last Sync</span><span style={value}>{formatTime(healthData.sms?.last_sync_at)}</span></div>
          </div>

          {/* COMMANDS */}
          <div style={section}>
            <div style={sectionTitle}>Commands</div>
            <div style={row}><span style={label}>Pending</span><span style={value}>{healthData.commands?.pending_commands ?? 0}</span></div>
            <div style={row}><span style={label}>Total</span><span style={value}>{healthData.commands?.total_commands ?? 0}</span></div>
            <div style={row}><span style={label}>Last Command</span><span style={value}>{formatTime(healthData.commands?.last_command_at)}</span></div>
            {healthData.commands?.lastResult && (
              <>
                <div style={row}><span style={label}>Last Result</span><span style={value}>{healthData.commands.lastResult.command_type}: {healthData.commands.lastResult.status}</span></div>
                <div style={row}><span style={label}>Result Time</span><span style={value}>{formatTime(healthData.commands.lastResult.created_at)}</span></div>
              </>
            )}
          </div>

          {/* FCM */}
          <div style={section}>
            <div style={sectionTitle}>FCM (Push Wake-up)</div>
            <div style={row}>
              <span style={label}>Registered</span>
              <span style={value}>
                {healthData.device.fcm_registered ? (
                  <span style={{ color: 'var(--status-success)' }}>● Yes</span>
                ) : (
                  <span style={{ color: 'var(--status-error)' }}>● No</span>
                )}
              </span>
            </div>
            <div style={row}><span style={label}>Token Updated</span><span style={value}>{formatTime(healthData.device.fcm_token_updated_at)}</span></div>
          </div>
        </div>
      )}
    </div>
  );
}
